import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Search, 
  Plus, 
  Filter,
  FileText,
  Clock,
  Users,
  Tag,
  Download,
  Printer,
  Eye,
  SlidersHorizontal
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { sopData } from '../../data/sopData';
import ManualCard from './ManualCard';
import ViewToggle from '../../../../components/shared/ViewToggle';
import { exportManualToPDF } from '../../../../utils/pdfExport';

const ManualsTab = () => {
  const [view, setView] = useState('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showFilters, setShowFilters] = useState(false);

  // Get unique categories
  const categories = ['all', ...new Set(sopData.manuals.map(manual => manual.category))];

  const handleDownload = (manual) => {
    try {
      const doc = exportManualToPDF(manual);
      doc.save(`${manual.title.toLowerCase().replace(/\s+/g, '-')}-manual.pdf`);
    } catch (error) {
      console.error('Failed to download manual:', error);
    }
  };

  const handlePrint = (manual) => {
    try {
      const doc = exportManualToPDF(manual);
      doc.autoPrint();
      doc.output('dataurlnewwindow');
    } catch (error) {
      console.error('Failed to print manual:', error);
    }
  };

  const ManualListItem = ({ manual }) => (
    <div className="bg-white rounded-lg shadow-sm hover:shadow-md transition-all p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className={`p-3 rounded-xl ${
            manual.category === 'Operations' ? 'bg-blue-50' :
            manual.category === 'Safety' ? 'bg-red-50' :
            manual.category === 'Quality' ? 'bg-green-50' :
            'bg-purple-50'
          }`}>
            <FileText className={`w-5 h-5 ${
              manual.category === 'Operations' ? 'text-blue-600' :
              manual.category === 'Safety' ? 'text-red-600' :
              manual.category === 'Quality' ? 'text-green-600' :
              'text-purple-600'
            }`} />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">{manual.title}</h3>
            <div className="flex items-center gap-4 mt-1">
              <span className="text-sm text-gray-500">{manual.category}</span>
              <span className="text-sm text-gray-500">•</span>
              <span className="text-sm text-gray-500">{manual.department}</span>
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-4 text-sm text-gray-500">
            <div className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              Updated {manual.lastUpdated}
            </div>
            <span className={`px-2 py-1 text-xs rounded-full ${
              manual.status === 'active' ? 'bg-green-100 text-green-700' :
              manual.status === 'draft' ? 'bg-yellow-100 text-yellow-700' :
              'bg-gray-100 text-gray-700'
            }`}>
              {manual.status}
            </span>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleDownload(manual)}
              className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
            >
              <Download className="w-4 h-4" />
            </button>
            <button
              onClick={() => handlePrint(manual)}
              className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
            >
              <Printer className="w-4 h-4" />
            </button>
            <Link
              to={`/sop/manuals/${manual.id}`}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              View Manual
            </Link>
          </div>
        </div>
      </div>
    </div>
  );

  const filteredManuals = sopData.manuals.filter(manual => {
    const matchesSearch = manual.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         manual.category.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || manual.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Standard Operating Manuals</h1>
          <p className="text-sm text-gray-500 mt-1">
            Create and manage standard operating procedures for your organization
          </p>
        </div>
        <div className="flex items-center gap-4">
          <ViewToggle view={view} onViewChange={setView} />
          <button 
            onClick={() => setShowFilters(!showFilters)}
            className="px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 flex items-center gap-2"
          >
            <SlidersHorizontal className="w-5 h-5" />
            Filters
          </button>
          <Link
            to="/sop/manuals/new"
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            New Manual
          </Link>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="space-y-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search manuals by title, category, or content..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* Category Filter */}
        <div className="flex overflow-x-auto pb-2 hide-scrollbar">
          <div className="flex gap-2">
            {categories.map(category => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  selectedCategory === category
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                <span className="whitespace-nowrap capitalize">
                  {category === 'all' ? 'All Categories' : category}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <AnimatePresence mode="wait">
        {view === 'grid' ? (
          <motion.div 
            key="grid"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {filteredManuals.map(manual => (
              <ManualCard 
                key={manual.id} 
                manual={manual}
                onDownload={handleDownload}
                onPrint={handlePrint}
              />
            ))}
          </motion.div>
        ) : (
          <motion.div
            key="list"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="space-y-4"
          >
            {filteredManuals.map(manual => (
              <ManualListItem 
                key={manual.id} 
                manual={manual}
                onDownload={handleDownload}
                onPrint={handlePrint}
              />
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ManualsTab; 